// Тонкая обёртка над window.Telegram.WebApp. SDK подгружается через <script> в index.html.

import { type Scheme, THEME_BG, type ThemePref, applyScheme, loadThemePref } from './theme';

type WebAppThemeParams = {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
};

type MainButton = {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  setText(text: string): void;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
};

type BackButton = {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
};

// Отступы, которые отдаёт клиент: safeAreaInset — вырез устройства (чёлка,
// системная полоса), contentSafeAreaInset — плавающая шапка самого Telegram,
// которая в полноэкранном режиме лежит ПОВЕРХ страницы.
type SafeAreaInset = { top: number; right: number; bottom: number; left: number };

type WebApp = {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name?: string; username?: string } };
  themeParams: WebAppThemeParams;
  colorScheme: 'light' | 'dark';
  platform?: string;
  ready(): void;
  expand(): void;
  close(): void;
  // Опциональные API (старые клиенты Telegram их не имеют).
  onEvent?(event: string, cb: () => void): void;
  offEvent?(event: string, cb: () => void): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  // Bot API 8.0. На старых клиентах и на Telegram Desktop отсутствуют либо
  // отвечают ошибкой UNSUPPORTED — вызывать только под isVersionAtLeast + try.
  isVersionAtLeast?(version: string): boolean;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  isFullscreen?: boolean;
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  // Bot API 7.7: свайп вниз по контенту закрывает приложение — на длинных
  // списках это срабатывает случайно.
  disableVerticalSwipes?(): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  MainButton: MainButton;
  BackButton: BackButton;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

export function getWebApp(): WebApp | null {
  return window.Telegram?.WebApp ?? null;
}

// SDK инжектит window.Telegram.WebApp в любом браузере, поэтому само его
// наличие ничего не значит: вне Telegram platform === 'unknown', а colorScheme
// всегда 'light'. Отличать нужно именно так, иначе dev-превью всегда светлое.
export function isInsideTelegram(): boolean {
  const wa = getWebApp();
  return !!wa && !!wa.platform && wa.platform !== 'unknown';
}

// Возвращает initData или пустую строку, если открыто вне Telegram (для dev в браузере).
export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

// Тема — выбор пользователя (тёмная по умолчанию), см. lib/theme.ts.
// data-scheme на <html> переключает токены в styles.css.
export function applyTelegramTheme(pref: ThemePref = loadThemePref()): Scheme {
  const scheme = applyScheme(pref);
  // Шапку и фон клиента красим под тот же тон, иначе вокруг приложения
  // остаётся чужая рамка Telegram и тема выглядит вставленной.
  // Клиенты старше 6.9 бросают на произвольном hex — тема от этого не зависит.
  try {
    const wa = getWebApp();
    wa?.setHeaderColor?.(THEME_BG[scheme]);
    wa?.setBackgroundColor?.(THEME_BG[scheme]);
  } catch {
    /* не поддерживается этим клиентом */
  }
  return scheme;
}

/**
 * Развернуть приложение на весь экран.
 *
 * expand() — это НЕ полный экран: он растягивает до максимума обычного режима,
 * оставляя шапку Telegram сверху. Полный экран — requestFullscreen (Bot API 8.0),
 * и там шапка превращается в плавающие кнопки поверх страницы.
 *
 * Всё под guard-ом: на клиентах старше 8.0 и на Telegram Desktop метода нет
 * либо он отвечает UNSUPPORTED, а необработанное исключение здесь оставило бы
 * пустой экран — это модульный код, он выполняется до первого рендера.
 */
export function enterFullscreen(): void {
  const wa = getWebApp();
  if (!wa || !isInsideTelegram()) return;
  try {
    wa.expand();
  } catch {
    /* не поддерживается этим клиентом */
  }
  try {
    if (wa.isVersionAtLeast?.('7.7')) wa.disableVerticalSwipes?.();
  } catch {
    /* не поддерживается этим клиентом */
  }
  try {
    if (wa.isVersionAtLeast?.('8.0')) wa.requestFullscreen?.();
  } catch {
    /* Desktop и старые клиенты: остаёмся в обычном режиме */
  }
}

/**
 * Держать CSS-переменные отступов в согласии с клиентом.
 *
 * Читаем значения из SDK, а не полагаемся на --tg-content-safe-area-inset-*:
 * переменные инжектит не каждый клиент, а объекты через SDK доступны везде, где
 * вообще есть режим. Фолбэк на env(safe-area-inset-*) живёт в styles.css, так
 * что вне Telegram переменные просто не выставляются и вёрстка не едет.
 */
export function applyViewportInsets(): void {
  const wa = getWebApp();
  if (!wa || !isInsideTelegram()) return;

  const sync = (): void => {
    const root = document.documentElement;
    const safe = wa.safeAreaInset;
    const content = wa.contentSafeAreaInset;
    if (!safe && !content) return;

    // Складываем: вырез устройства + шапка Telegram поверх него.
    const top = (safe?.top ?? 0) + (content?.top ?? 0);
    const bottom = (safe?.bottom ?? 0) + (content?.bottom ?? 0);
    root.style.setProperty('--app-inset-top', `${top}px`);
    root.style.setProperty('--app-inset-bottom', `${bottom}px`);
    root.dataset.fullscreen = wa.isFullscreen ? 'on' : 'off';
  };

  sync();
  for (const e of [
    'safeAreaChanged',
    'contentSafeAreaChanged',
    'fullscreenChanged',
    'viewportChanged',
  ]) {
    wa.onEvent?.(e, sync);
  }
}

// Тактильный отклик: безопасно вызывать вне Telegram (просто no-op).
export function haptic(
  style: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning' = 'light',
): void {
  const hf = getWebApp()?.HapticFeedback;
  if (!hf) return;
  if (style === 'success' || style === 'error' || style === 'warning') {
    hf.notificationOccurred(style);
  } else {
    hf.impactOccurred(style);
  }
}
